<div class="space-y-6">
  <app-page-header [backTo]="['/users']" backLabel="Retour à la liste" />

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      (retry)="fetch()"
    />
  } @else if (user(); as u) {
    <!-- Identity card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-4">
          @if (u.avatarUrl) {
            <img [src]="u.avatarUrl" [alt]="fullName()" class="h-16 w-16 rounded-full object-cover" />
          } @else {
            <span class="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-base font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
              {{ initials() }}
            </span>
          }
          <div class="min-w-0">
            <h1 class="text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">{{ fullName() }}</h1>
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">&#64;{{ u.username }}</p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              @if (u.active) {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-2.5 py-1 text-xs font-medium text-success-700 dark:bg-success-500/15 dark:text-success-400">
                  <span class="h-1.5 w-1.5 rounded-full bg-success-500"></span> Actif
                </span>
              } @else {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  <span class="h-1.5 w-1.5 rounded-full bg-gray-400"></span> Inactif
                </span>
              }
              @if (u.changePassword) {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-warning-50 px-2.5 py-1 text-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                  Doit changer son mot de passe
                </span>
              }
              @for (r of businessRoles(); track r) {
                <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ r }}</span>
              }
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <a
            [routerLink]="['/users', u.id, 'edit']"
            class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Modifier
          </a>
          <button type="button" (click)="openRegen()" class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
            Régénérer le mot de passe
          </button>
          <button type="button" (click)="openDelete()" class="inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10">
            Supprimer
          </button>
        </div>
      </div>
    </section>

    <!-- Rôles assignés -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0">
          <h2 class="text-sm font-semibold text-gray-800 dark:text-white/90">Rôles assignés</h2>
          <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Liste complète des rôles Keycloak de ce compte (métier + système).
          </p>
          <div class="mt-3 flex flex-wrap gap-1.5">
            @if (u.roles.length) {
              @for (r of u.roles; track r) {
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                  [class.bg-brand-50]="isBusinessRole(r)"
                  [class.text-brand-700]="isBusinessRole(r)"
                  [class.dark:bg-brand-500/15]="isBusinessRole(r)"
                  [class.dark:text-brand-400]="isBusinessRole(r)"
                  [class.bg-gray-100]="!isBusinessRole(r)"
                  [class.text-gray-600]="!isBusinessRole(r)"
                  [class.dark:bg-gray-800]="!isBusinessRole(r)"
                  [class.dark:text-gray-400]="!isBusinessRole(r)"
                >
                  {{ r }}
                </span>
              }
            } @else {
              <span class="text-xs text-gray-400">Aucun rôle assigné.</span>
            }
          </div>
        </div>
        <button type="button" (click)="openRoles()" class="inline-flex items-center gap-2 self-start rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
          Gérer les rôles
        </button>
      </div>
    </section>

    <!-- Info grid -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Contact</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90 break-all">{{ u.email ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Téléphone</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ u.phone ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Sexe</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              @switch (u.sex) {
                @case ('HOMME') { Homme }
                @case ('FEMME') { Femme }
                @default { — }
              }
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Adresse</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ u.address ?? '—' }}</dd>
          </div>
        </dl>
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Métadonnées</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">ID</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">#{{ u.id }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Créé le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ u.createdAt ? (u.createdAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Modifié le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ u.updatedAt ? (u.updatedAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Notif sub. ID</dt>
            <dd class="mt-1 text-xs text-gray-500 break-all dark:text-gray-400">{{ u.notifSubId ?? '—' }}</dd>
          </div>
          @if (u.permissions?.length) {
            <div class="sm:col-span-2">
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Permissions</dt>
              <dd class="mt-1.5 flex flex-wrap gap-1">
                @for (p of u.permissions; track p) {
                  <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ p }}</span>
                }
              </dd>
            </div>
          }
        </dl>
      </section>
    </div>
  }
</div>

<!-- Roles management modal -->
<app-modal [isOpen]="rolesOpen()" (close)="closeRoles()" className="w-full max-w-lg rounded-2xl bg-white p-6 dark:bg-gray-dark">
  <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Gérer les rôles</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Cocher / décocher pour assigner ou retirer un rôle à <span class="font-medium text-gray-700 dark:text-gray-300">{{ fullName() }}</span>.
  </p>

  <div class="mt-4">
    <input
      type="text"
      [formControl]="rolesFilterCtrl"
      placeholder="Filtrer les rôles…"
      class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
    />
  </div>

  <div class="mt-3 max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
    @if (rolesLoading()) {
      <div class="px-4 py-10 text-center">
        <svg class="mx-auto h-5 w-5 animate-spin text-brand-500" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
          <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
        </svg>
        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">Chargement des rôles…</p>
      </div>
    } @else if (visibleRoles().length === 0) {
      <p class="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">Aucun rôle.</p>
    } @else {
      <ul class="divide-y divide-gray-200 dark:divide-gray-800">
        @for (r of visibleRoles(); track r.name) {
          <li>
            <label class="flex cursor-pointer items-start gap-3 px-4 py-3 transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
              <input
                type="checkbox"
                [checked]="isRoleSelected(r.name)"
                (change)="toggleRole(r.name)"
                class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20 dark:border-gray-600"
              />
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-1.5">
                  <span class="font-mono text-sm text-gray-800 dark:text-white/90">{{ r.name }}</span>
                  <span
                    class="rounded-full px-1.5 py-0.5 text-[9px] font-medium"
                    [class.bg-brand-50]="isBusinessRole(r.name)"
                    [class.text-brand-700]="isBusinessRole(r.name)"
                    [class.dark:bg-brand-500/15]="isBusinessRole(r.name)"
                    [class.dark:text-brand-400]="isBusinessRole(r.name)"
                    [class.bg-gray-100]="!isBusinessRole(r.name)"
                    [class.text-gray-600]="!isBusinessRole(r.name)"
                    [class.dark:bg-gray-800]="!isBusinessRole(r.name)"
                    [class.dark:text-gray-400]="!isBusinessRole(r.name)"
                  >
                    {{ isBusinessRole(r.name) ? 'métier' : 'système' }}
                  </span>
                </span>
                @if (r.description) {
                  <span class="block text-xs text-gray-500 dark:text-gray-400">{{ r.description }}</span>
                }
              </span>
            </label>
          </li>
        }
      </ul>
    }
  </div>

  @if (rolesError(); as msg) {
    <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
  }

  <div class="mt-6 flex items-center justify-between">
    <span class="text-xs text-gray-500 dark:text-gray-400">
      {{ selectedRoles().size }} rôle(s) coché(s)
      @if (rolesDirty()) { <span class="ml-1 text-warning-700 dark:text-warning-400">• modifications non enregistrées</span> }
    </span>
    <div class="flex gap-2">
      <button type="button" (click)="closeRoles()" [disabled]="rolesSubmitting()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="button" (click)="saveRoles()" [disabled]="rolesSubmitting() || !rolesDirty()" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300">
        @if (rolesSubmitting()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
          </svg>
          Enregistrement…
        } @else { Enregistrer }
      </button>
    </div>
  </div>
</app-modal>

<!-- Regen password modal -->
<app-modal [isOpen]="regenOpen()" (close)="closeRegen()" className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-dark">
  @if (!regenResult()) {
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Régénérer le mot de passe ?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Un nouveau mot de passe sera généré pour <span class="font-medium text-gray-700 dark:text-gray-300">{{ fullName() }}</span>. L'ancien deviendra invalide immédiatement.
    </p>
    @if (regenError(); as msg) {
      <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
    }
    <div class="mt-6 flex justify-end gap-2">
      <button type="button" (click)="closeRegen()" [disabled]="regenLoading()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="button" (click)="confirmRegen()" [disabled]="regenLoading()" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
        @if (regenLoading()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
          </svg>
          Régénération…
        } @else { Régénérer }
      </button>
    </div>
  } @else {
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Nouveau mot de passe</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Copiez et transmettez-le à l'utilisateur. Il ne sera plus affiché.
    </p>
    <div class="mt-4 flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <code class="flex-1 break-all text-sm text-gray-800 dark:text-white/90">{{ regenResult() }}</code>
      <button type="button" (click)="copyResult()" title="Copier" class="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">
        Copier
      </button>
    </div>
    <div class="mt-6 flex justify-end">
      <button type="button" (click)="closeRegen()" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">Fermer</button>
    </div>
  }
</app-modal>

<!-- Delete confirmation -->
<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer l'utilisateur ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  Cette action est irréversible. L'utilisateur <span class="font-medium text-gray-700 dark:text-gray-300">{{ fullName() }}</span> sera supprimé et ne pourra plus se connecter.
</app-confirm-modal>
